import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  TimePicker,
  Typography,
  Upload,
} from 'antd';
import {
  CoffeeOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  LogoutOutlined,
  PlusOutlined,
  ShopOutlined,
  StarOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { InvoiceNotificationService } from '../services/invoiceNotificationService';
import { DishService } from '../services/dishService';
import { CustomizationService } from '../services/CustomizationService';
import { InvoiceNotification } from '../types/invoiceNotification';
import { Customization } from '../types/customization';
import { Branch2Dish, Branch2DishFormValues } from '../types/branch2Dish';
import { useNavigate } from 'react-router-dom';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import IngredientManagement from './IngredientManagement';
import '../styles/AdminPages.css';

const { Title, Text } = Typography;
const invoiceService = new InvoiceNotificationService();
const dishService = new DishService();
const customizationService = new CustomizationService();
const firebaseStorage = getStorage();

// ─── Constants ───────────────────────────────────────────────────────────────

const BRANCH2_AUTH_KEY = 'isBranch2Staff8Am';
const BRANCH2_LOGIN_TIME_KEY = 'branch2LoginTime8Am';

/**
 * Firestore document IDs từ collection `storeLocations`
 * Cơ sở 1 — 34 Tăng Bạt Hổ: a2tA1ceTui7rDVPmV3Mo  (store_uid: ...cd077)
 * Cơ sở 2 — 241 Xuân Thủy:  HWWFsMlmYwhNLlau1Ryf  (store_uid: ...cd079)
 */
export const STORE_ID_BRANCH1 = 'a2tA1ceTui7rDVPmV3Mo';
export const STORE_ID_BRANCH2 = 'HWWFsMlmYwhNLlau1Ryf';
const STORE_UID_BRANCH2 = 'e311c0ef-ee4a-4355-af62-0ae4c88cd079';
const BRANCH2_STORE_NAME = '241 Xuân Thủy';
const BRANCH2_STORE_LABEL = `Cơ sở 2 — ${BRANCH2_STORE_NAME}`;
const REVENUE_SOURCE_8AM = '8am';
const REVENUE_SOURCE_NHA_NAM = 'nhã nam';
const REVENUE_SOURCE_OPTIONS = [
  { value: REVENUE_SOURCE_8AM, label: '8am' },
  { value: REVENUE_SOURCE_NHA_NAM, label: 'Nhã Nam' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatVnd = (value: number | undefined | null) => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value);
};

const nowTimeStr = () => dayjs().format('HH:mm');

const parseTimeToDayjs = (time?: string) => {
  if (!time) return undefined;
  const [hourRaw, minuteRaw] = time.split(':');
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return undefined;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return undefined;
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

/** Tạo invoiceCode ngẫu nhiên cho phiếu nhập tay, prefix CS2- */
const generateInvoiceCode = () =>
  `CS2-${dayjs().format('YYYYMMDD')}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

// ─── Form values ─────────────────────────────────────────────────────────────

interface InvoiceFormValues {
  checkInDate: Dayjs;
  checkInTime?: Dayjs;
  revenueSource: string;
  invoiceCode?: string;
  items: InvoiceLineFormValues[];
}

interface InvoiceLineFormValues {
  itemId: string;
  itemName: string;
  selectedOptions?: InvoiceOptionFormValue[];
  quantity: number;
  unitPrice?: number;
  note?: string;
}

interface InvoiceOptionFormValue {
  optionId: string;
  optionName: string;
  unitPrice?: number;
}

interface MenuItem {
  itemId: string;
  itemName: string;
  itemGroup?: string;
  defaultPrice?: number;
}

interface InvoiceOptionItem {
  optionId: string;
  optionName: string;
  defaultPrice?: number;
}

type Branch2EntryMode = 'invoice' | 'ingredient' | 'menuManagement';

const generateBranch2DishCode = () =>
  `CS2-${dayjs().format('YYYYMMDD')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

// ─── Component ───────────────────────────────────────────────────────────────

const Branch2InvoiceEntry: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<InvoiceFormValues>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InvoiceNotification | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceNotification[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [customizations, setCustomizations] = useState<Customization[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
  const [entryMode, setEntryMode] = useState<Branch2EntryMode>('invoice');
  const isMountedRef = useRef(true);

  // ── Branch2 menu management state ──
  const [branch2Dishes, setBranch2Dishes] = useState<Branch2Dish[]>([]);
  const [branch2Loading, setBranch2Loading] = useState(false);
  const [branch2ModalOpen, setBranch2ModalOpen] = useState(false);
  const [editingBranch2Dish, setEditingBranch2Dish] = useState<Branch2Dish | null>(null);
  const [branch2Saving, setBranch2Saving] = useState(false);
  const [branch2ImageUrl, setBranch2ImageUrl] = useState<string>('');
  const [branch2ImageLoading, setBranch2ImageLoading] = useState(false);
  const [branch2Form] = Form.useForm<Branch2DishFormValues>();

  // ── Computed ──

  const monthInvoices = useMemo(() => {
    const monthStr = selectedMonth.format('YYYY/MM');
    return invoices.filter((inv) => {
      if (!inv.checkInDate) return false;
      // checkInDate format: "DD/MM/YYYY" hoặc "YYYY-MM-DD"
      const parts = (inv.checkInDate || '').split('/');
      if (parts.length === 3) {
        // DD/MM/YYYY
        return `${parts[2]}/${parts[1]}` === monthStr;
      }
      // fallback
      const d = dayjs(inv.checkInDate);
      return d.isValid() && d.format('YYYY/MM') === monthStr;
    });
  }, [invoices, selectedMonth]);

  const totalQty = useMemo(
    () => monthInvoices.reduce((sum, inv) => sum + (Number(inv.quantity) || 0), 0),
    [monthInvoices],
  );

  const totalRevenue = useMemo(
    () => monthInvoices.reduce((sum, inv) => sum + (Number(inv.lineTotal) || 0), 0),
    [monthInvoices],
  );

  const menuItemOptions = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>();
    for (const m of menuItems) {
      const groupKey = m.itemGroup || 'Khác';
      const arr = groups.get(groupKey) ?? [];
      arr.push({ value: m.itemId, label: m.itemName });
      groups.set(groupKey, arr);
    }
    const priorityGroups = ['Món CS2'];
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const aPriority = priorityGroups.indexOf(a) >= 0 ? -1 : 0;
        const bPriority = priorityGroups.indexOf(b) >= 0 ? -1 : 0;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return a.localeCompare(b, 'vi');
      })
      .map(([label, options]) => ({ label, options }));
  }, [menuItems]);

  const menuItemLookup = useMemo(() => {
    const nameMap = new Map<string, string>();
    const priceMap = new Map<string, number>();
    for (const item of menuItems) {
      nameMap.set(item.itemId, item.itemName);
      if (item.defaultPrice) {
        priceMap.set(item.itemId, item.defaultPrice);
      }
    }
    return { nameMap, priceMap };
  }, [menuItems]);

  const normalizeCode = (value?: string) => (value || '').trim().toLowerCase();

  const extractOptionsFromCustomizations = useCallback((items: Customization[]): InvoiceOptionItem[] => {
    const seen = new Map<string, InvoiceOptionItem>();
    items.forEach((customization) => {
      (customization.groups || []).forEach((group) => {
        (group.dishCodes || []).forEach((dish) => {
          const optionId = dish.code?.trim();
          const optionName = dish.name?.trim();
          if (!optionId || !optionName) return;
          if (dish.isActive === false) return;
          const price = Number(dish.price || 0);
          const existed = seen.get(optionId);
          if (!existed) {
            seen.set(optionId, {
              optionId,
              optionName,
              defaultPrice: price > 0 ? price : undefined,
            });
            return;
          }

          if ((!existed.defaultPrice || existed.defaultPrice <= 0) && price > 0) {
            seen.set(optionId, {
              ...existed,
              defaultPrice: price,
            });
          }
        });
      });
    });

    return Array.from(seen.values())
      .sort((a, b) => a.optionName.localeCompare(b.optionName, 'vi'));
  }, []);

  const dishOptionsByCode = useMemo(() => {
    const map = new Map<string, InvoiceOptionItem[]>();

    customizations.forEach((customization) => {
      const options = extractOptionsFromCustomizations([customization]);
      if (!options.length) return;

      (customization.dishCode || []).forEach((dishCode) => {
        const normalizedDishCode = normalizeCode(dishCode);
        if (!normalizedDishCode) return;

        const existed = map.get(normalizedDishCode) || [];
        const merged = new Map<string, InvoiceOptionItem>([
          ...existed.map((opt) => [opt.optionId, opt] as const),
        ]);

        options.forEach((option) => {
          const current = merged.get(option.optionId);
          if (!current) {
            merged.set(option.optionId, option);
            return;
          }

          if ((!current.defaultPrice || current.defaultPrice <= 0) && (option.defaultPrice || 0) > 0) {
            merged.set(option.optionId, {
              ...current,
              defaultPrice: option.defaultPrice,
            });
          }
        });

        map.set(
          normalizedDishCode,
          Array.from(merged.values())
            .sort((a, b) => a.optionName.localeCompare(b.optionName, 'vi')),
        );
      });
    });

    return map;
  }, [customizations, extractOptionsFromCustomizations]);

  const optionCatalog = useMemo(() => {
    const map = new Map<string, string>();
    dishOptionsByCode.forEach((options) => {
      options.forEach((option) => {
        map.set(option.optionId, option.optionName);
      });
    });
    return map;
  }, [dishOptionsByCode]);

  // ── Data loading ──

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const all = await invoiceService.getNotificationsByStore({
        storeUid: STORE_UID_BRANCH2,
        storeAliases: [BRANCH2_STORE_NAME, STORE_ID_BRANCH2],
      });
      if (!isMountedRef.current) return;
      setInvoices(all);
    } catch {
      message.error('Không thể tải danh sách hóa đơn.');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  const loadMenuItems = useCallback(async () => {
    try {
      const [allDishes, b2Dishes] = await Promise.all([
        dishService.getAllDishes(),
        dishService.getBranch2Dishes(),
      ]);
      if (!isMountedRef.current) return;

      const mainItems: MenuItem[] = allDishes
        .filter((d) => !!d.code && !!d.name && (d as any).branch !== 'branch2')
        .map((d) => ({
          itemId: d.code.trim(),
          itemName: d.name.trim(),
          itemGroup: (d.groupName || d.group || '').trim() || 'Món chung',
          defaultPrice: d.price > 0 ? d.price : undefined,
        }));

      const branch2Items: MenuItem[] = b2Dishes
        .filter((d) => !!d.code && !!d.name && d.isActive !== false)
        .map((d) => ({
          itemId: d.code.trim(),
          itemName: d.name.trim(),
          itemGroup: 'Món CS2',
          defaultPrice: d.price > 0 ? d.price : undefined,
        }));

      setMenuItems(
        [...mainItems, ...branch2Items].sort((a, b) => a.itemName.localeCompare(b.itemName, 'vi')),
      );
    } catch {
      /* ignore */
    }
  }, []);

  const loadBranch2Dishes = useCallback(async () => {
    setBranch2Loading(true);
    try {
      const dishes = await dishService.getBranch2Dishes();
      if (!isMountedRef.current) return;
      setBranch2Dishes(dishes);
    } catch {
      message.error('Không thể tải danh sách món cơ sở 2.');
    } finally {
      if (isMountedRef.current) setBranch2Loading(false);
    }
  }, []);

  const loadCustomizations = useCallback(async () => {
    try {
      const rows = await customizationService.getAllCustomizations();
      if (!isMountedRef.current) return;
      setCustomizations(rows || []);
    } catch {
      setCustomizations([]);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadInvoices();
    loadMenuItems();
    loadCustomizations();
    loadBranch2Dishes();
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ──

  const openAddModal = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      checkInDate: dayjs(),
      checkInTime: dayjs(),
      revenueSource: REVENUE_SOURCE_8AM,
      invoiceCode: generateInvoiceCode(),
      items: [{ itemId: '', itemName: '', selectedOptions: [], quantity: 1 }],
    });
    setModalOpen(true);
  };

  const openEditModal = (inv: InvoiceNotification) => {
    setEditingItem(inv);
    // Parse checkInDate DD/MM/YYYY → Dayjs
    let dateVal = dayjs();
    if (inv.checkInDate) {
      const parts = inv.checkInDate.split('/');
      if (parts.length === 3) {
        dateVal = dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);
      } else {
        dateVal = dayjs(inv.checkInDate);
      }
    }
    form.setFieldsValue({
      checkInDate: dateVal,
      checkInTime: parseTimeToDayjs(inv.checkInTime),
      revenueSource: (inv.channel || '').trim() || REVENUE_SOURCE_8AM,
      invoiceCode: inv.invoiceCode || '',
      items: [{
        itemId: inv.itemId || '',
        itemName: inv.itemName || '',
        selectedOptions: (inv.selectedOptions || [])
          .map((option) => {
            const optionId = (option.optionId || option.optionName || '').trim();
            const optionName = (option.optionName || option.optionId || '').trim();
            if (!optionId || !optionName) return null;

            const optionQty = Number(option.quantity || inv.quantity || 1) || 1;
            const fallbackPrice =
              option.price !== undefined
                ? Number(option.price || 0)
                : Number(option.amount || 0) / optionQty;

            return {
              optionId,
              optionName,
              unitPrice: Number.isFinite(fallbackPrice) && fallbackPrice > 0 ? fallbackPrice : undefined,
            };
          })
          .filter(Boolean) as InvoiceOptionFormValue[],
        quantity: inv.quantity || 1,
        unitPrice: inv.unitPrice || undefined,
        note: inv.note || '',
      }],
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const dateStr = values.checkInDate.format('DD/MM/YYYY');
      const timeStr = values.checkInTime?.format('HH:mm') || nowTimeStr();
      const revenueSource = (values.revenueSource || '').trim() || REVENUE_SOURCE_8AM;
      const invoiceCode = values.invoiceCode || generateInvoiceCode();
      const rows = (values.items || []).filter((row) => !!row.itemId);

      if (!rows.length) {
        message.error('Vui lòng chọn ít nhất 1 món.');
        return;
      }

      const buildPayload = (row: InvoiceLineFormValues): InvoiceNotification => {
        const quantity = Number(row.quantity || 1);
        const unitPrice = Number(row.unitPrice || 0);
        const selectedOptions = (row.selectedOptions || [])
          .map((option) => {
            const optionId = (option.optionId || option.optionName || '').trim();
            if (!optionId) return null;
            const optionName = (option.optionName || optionCatalog.get(optionId) || optionId).trim();
            const optionPrice = Number(option.unitPrice || 0);
            const optionAmount = optionPrice > 0 ? quantity * optionPrice : undefined;

            return {
              optionId,
              optionName,
              quantity,
              unit: 'món',
              price: optionPrice || undefined,
              amount: optionAmount,
            };
          })
          .filter(Boolean) as NonNullable<InvoiceNotification['selectedOptions']>;

        const itemAmount = unitPrice > 0 ? quantity * unitPrice : 0;
        const optionAmount = selectedOptions.reduce((sum, option) => sum + Number(option.amount || 0), 0);
        const totalAmount = itemAmount + optionAmount;
        const lineTotal = totalAmount > 0 ? totalAmount : undefined;
        const optionSummary = selectedOptions.map((option) => option.optionName).join(', ');
        const itemName = row.itemName || menuItemLookup.nameMap.get(row.itemId) || row.itemId;

        return {
          invoiceCode,
          checkInDate: dateStr,
          checkInTime: timeStr,
          store: BRANCH2_STORE_NAME,
          storeUid: STORE_UID_BRANCH2,
          itemId: row.itemId || '',
          itemName,
          quantity,
          unit: 'ly',
          unitPrice: unitPrice || undefined,
          lineTotal,
          note: row.note || '',
          channel: revenueSource,
          selectedOptions,
          optionSummary,
          notificationTitle: `[CS2] ${itemName}`,
          notificationContent: `${itemName} x${quantity} · ${dateStr}${optionSummary ? ` · ${optionSummary}` : ''} · Nguồn thu: ${revenueSource}`,
          isActive: true,
        };
      };

      if (editingItem?.id) {
        await invoiceService.updateNotification(editingItem.id, buildPayload(rows[0]));
        message.success('Đã cập nhật hóa đơn!');
      } else {
        for (const row of rows) {
          await invoiceService.addNotification(buildPayload(row));
        }
        message.success(`Đã thêm ${rows.length} dòng hóa đơn!`);
      }

      setModalOpen(false);
      form.resetFields();
      await loadInvoices();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('Lưu thất bại. Vui lòng thử lại.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      await invoiceService.deleteNotification(id);
      message.success('Đã xóa!');
      await loadInvoices();
    } catch {
      message.error('Xóa thất bại.');
    }
  };

  // ── Branch2 menu management handlers ──

  const handleBranch2ImageUpload = async (file: File) => {
    setBranch2ImageLoading(true);
    try {
      const storageRef = ref(firebaseStorage, `8am-branch2-dish-images/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setBranch2ImageUrl(downloadUrl);
      branch2Form.setFieldsValue({ imageUrl: downloadUrl });
      message.success('Tải ảnh thành công!');
    } catch {
      message.error('Tải ảnh thất bại.');
    } finally {
      setBranch2ImageLoading(false);
    }
    return false;
  };

  const handleDeleteBranch2Image = async () => {
    if (!branch2ImageUrl) return;
    try {
      const imageRef = ref(firebaseStorage, branch2ImageUrl);
      await deleteObject(imageRef);
    } catch { /* ignore if not found */ }
    setBranch2ImageUrl('');
    branch2Form.setFieldsValue({ imageUrl: '' });
  };

  const openBranch2AddModal = () => {
    setEditingBranch2Dish(null);
    branch2Form.resetFields();
    setBranch2ImageUrl('');
    branch2Form.setFieldsValue({ isSignature: false });
    setBranch2ModalOpen(true);
  };

  const openBranch2EditModal = (dish: Branch2Dish) => {
    setEditingBranch2Dish(dish);
    branch2Form.setFieldsValue({
      name: dish.name,
      description: dish.description || '',
      price: dish.price,
      isSignature: dish.isSignature || false,
      group: dish.groupName || dish.group || '',
    });
    setBranch2ImageUrl(dish.imageUrl || '');
    setBranch2ModalOpen(true);
  };

  const handleBranch2DishSave = async () => {
    try {
      const values = await branch2Form.validateFields();
      setBranch2Saving(true);

      const groupVal = (values.group || '').trim();
      const dishData: Omit<Branch2Dish, 'id'> = {
        code: editingBranch2Dish?.code || generateBranch2DishCode(),
        name: values.name.trim(),
        price: Number(values.price || 0),
        description: (values.description || '').trim(),
        isSignature: values.isSignature || false,
        branch: 'branch2',
        isActive: true,
        unit: 'ly',
        vat: 0,
        preparationTime: 0,
        priceConfiguration: {
          allowPriceEdit: true,
          requireQuantity: true,
          allowRemoveWithoutPermission: true,
        },
        city: '',
        ...(branch2ImageUrl ? { imageUrl: branch2ImageUrl } : {}),
        ...(groupVal ? { group: groupVal, groupName: groupVal } : {}),
      };

      if (editingBranch2Dish?.id) {
        await dishService.updateBranch2Dish(editingBranch2Dish.id, dishData);
        message.success('Đã cập nhật món!');
      } else {
        await dishService.addBranch2Dish(dishData);
        message.success('Đã thêm món mới!');
      }

      setBranch2ModalOpen(false);
      branch2Form.resetFields();
      setBranch2ImageUrl('');
      await Promise.all([loadBranch2Dishes(), loadMenuItems()]);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('Lưu thất bại. Vui lòng thử lại.');
    } finally {
      setBranch2Saving(false);
    }
  };

  const handleDeleteBranch2Dish = async (id?: string) => {
    if (!id) return;
    try {
      await dishService.deleteBranch2Dish(id);
      message.success('Đã xóa món!');
      await Promise.all([loadBranch2Dishes(), loadMenuItems()]);
    } catch {
      message.error('Xóa thất bại.');
    }
  };

  const handleToggleBranch2DishActive = async (dish: Branch2Dish) => {
    if (!dish.id) return;
    try {
      await dishService.updateBranch2Dish(dish.id, { isActive: !dish.isActive });
      message.success(dish.isActive ? 'Đã ẩn món!' : 'Đã hiện món!');
      await Promise.all([loadBranch2Dishes(), loadMenuItems()]);
    } catch {
      message.error('Cập nhật thất bại.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(BRANCH2_AUTH_KEY);
    localStorage.removeItem(BRANCH2_LOGIN_TIME_KEY);
    navigate('/login');
  };

  // ── Table columns ──

  const columns = [
    {
      title: 'Ngày',
      dataIndex: 'checkInDate',
      key: 'checkInDate',
      width: 110,
      render: (v: string, rec: InvoiceNotification) =>
        `${v || '-'} ${rec.checkInTime ? rec.checkInTime : ''}`.trim(),
      sorter: (a: InvoiceNotification, b: InvoiceNotification) => {
        const toMs = (d?: string) => {
          if (!d) return 0;
          const parts = d.split('/');
          if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
          return new Date(d).getTime();
        };
        return toMs(a.checkInDate) - toMs(b.checkInDate);
      },
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Mã hóa đơn',
      dataIndex: 'invoiceCode',
      key: 'invoiceCode',
      width: 160,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v || '-'}</Text>,
    },
    {
      title: 'Nguồn thu',
      dataIndex: 'channel',
      key: 'channel',
      width: 110,
      render: (v: string) => {
        const normalized = (v || '').trim().toLowerCase();
        if (normalized === REVENUE_SOURCE_8AM) return <Tag color="blue">8am</Tag>;
        if (normalized === REVENUE_SOURCE_NHA_NAM) return <Tag color="purple">Nhã Nam</Tag>;
        return '-';
      },
    },
    {
      title: 'Tên món',
      dataIndex: 'itemName',
      key: 'itemName',
      render: (name: string, rec: InvoiceNotification) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || '-'}</Text>
          {rec.itemId ? <Text type="secondary" style={{ fontSize: 12 }}>Mã: {rec.itemId}</Text> : null}
        </Space>
      ),
    },
    {
      title: 'SL',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      align: 'right' as const,
      render: (v: number) => <Text strong>{Number(v || 0)}</Text>,
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (v: number) => v ? formatVnd(v) : '-',
    },
    {
      title: 'Thành tiền',
      dataIndex: 'lineTotal',
      key: 'lineTotal',
      width: 130,
      align: 'right' as const,
      render: (v: number) => v ? <Text strong style={{ color: '#1677ff' }}>{formatVnd(v)}</Text> : '-',
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      render: (v: string) => v || '-',
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: any, rec: InvoiceNotification) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(rec)} />
          <Popconfirm
            title="Xóa dòng hóa đơn này?"
            onConfirm={() => handleDelete(rec.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Branch2 dish table columns ──

  const branch2DishColumns = [
    {
      title: 'Ảnh',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (url: string) =>
        url ? (
          <Image src={url} width={56} height={56} style={{ objectFit: 'cover', borderRadius: 8 }} />
        ) : (
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 8,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CoffeeOutlined style={{ fontSize: 20, color: '#bbb' }} />
          </div>
        ),
    },
    {
      title: 'Tên món',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, rec: Branch2Dish) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{name}</Text>
            {rec.isSignature ? <Tag color="gold"><StarOutlined /> Signature</Tag> : null}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>Mã: {rec.code}</Text>
        </Space>
      ),
    },
    {
      title: 'Nguyên liệu',
      dataIndex: 'description',
      key: 'description',
      render: (v: string) => v || '-',
    },
    {
      title: 'Giá',
      dataIndex: 'price',
      key: 'price',
      width: 130,
      align: 'right' as const,
      render: (v: number) => <Text strong style={{ color: '#1677ff' }}>{formatVnd(v)}</Text>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (active: boolean, rec: Branch2Dish) => (
        <Switch
          checked={active !== false}
          onChange={() => handleToggleBranch2DishActive(rec)}
          checkedChildren="Hiện"
          unCheckedChildren="Ẩn"
          size="small"
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_: any, rec: Branch2Dish) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openBranch2EditModal(rec)} />
          <Popconfirm
            title="Xóa món này?"
            onConfirm={() => handleDeleteBranch2Dish(rec.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ──

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f7fb',
        padding: '24px',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
          padding: '16px 20px',
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #f0f0f0',
        }}
      >
        <Space align="center" style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div
            style={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShopOutlined style={{ fontSize: 22, color: '#fff' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
              {entryMode === 'invoice'
                ? 'Nhập hóa đơn bán hàng'
                : entryMode === 'menuManagement'
                  ? 'Quản lý món cơ sở 2'
                  : 'Nhập nguyên liệu'}
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {BRANCH2_STORE_LABEL}
            </Text>
          </div>
        </Space>
        <Space wrap style={{ flexShrink: 0 }}>
          <Button
            type={entryMode === 'invoice' ? 'primary' : 'default'}
            onClick={() => setEntryMode('invoice')}
          >
            Nhập hóa đơn
          </Button>
          <Button
            type={entryMode === 'ingredient' ? 'primary' : 'default'}
            onClick={() => setEntryMode('ingredient')}
          >
            Nhập nguyên liệu
          </Button>
          <Button
            type={entryMode === 'menuManagement' ? 'primary' : 'default'}
            icon={<CoffeeOutlined />}
            onClick={() => setEntryMode('menuManagement')}
          >
            Quản lý món CS2
          </Button>
          <Button
            icon={<LogoutOutlined />}
            onClick={handleLogout}
          >
            Đăng xuất
          </Button>
        </Space>
      </div>

      {entryMode === 'menuManagement' ? (
        <>
          {/* Branch2 menu management */}
          <Row style={{ marginBottom: 20 }}>
            <Col span={24} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">
                Tổng cộng {branch2Dishes.length} món riêng cơ sở 2
              </Text>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openBranch2AddModal}
                size="large"
                className="admin-button"
              >
                Thêm món mới
              </Button>
            </Col>
          </Row>

          <Card
            className="admin-table"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              dataSource={branch2Dishes}
              columns={branch2DishColumns}
              rowKey={(d) => d.id || d.code}
              loading={branch2Loading}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              locale={{ emptyText: 'Chưa có món nào. Bấm "Thêm món mới" để bắt đầu.' }}
              style={{ borderRadius: 12, overflow: 'hidden' }}
              scroll={{ x: 700 }}
            />
          </Card>

          {/* Branch2 dish add/edit modal */}
          <Modal
            title={
              <Space>
                <CoffeeOutlined style={{ color: '#f59e0b' }} />
                {editingBranch2Dish ? 'Sửa món cơ sở 2' : 'Thêm món mới – Cơ sở 2'}
              </Space>
            }
            open={branch2ModalOpen}
            onCancel={() => {
              setBranch2ModalOpen(false);
              branch2Form.resetFields();
              setBranch2ImageUrl('');
            }}
            onOk={handleBranch2DishSave}
            okText={editingBranch2Dish ? 'Cập nhật' : 'Lưu'}
            cancelText="Hủy"
            confirmLoading={branch2Saving}
            width={600}
            destroyOnClose
          >
            <Form form={branch2Form} layout="vertical">
              <Form.Item
                name="name"
                label="Tên món"
                rules={[{ required: true, message: 'Vui lòng nhập tên món!' }]}
              >
                <Input placeholder="Ví dụ: Pina Colada" size="large" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Nguyên liệu / Mô tả"
              >
                <Input.TextArea
                  rows={2}
                  placeholder="Ví dụ: Malibu, Pineapple juice, Coconut milk"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={14}>
                  <Form.Item
                    name="price"
                    label="Giá (VND)"
                    rules={[{ required: true, message: 'Vui lòng nhập giá!' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      placeholder="100000"
                      size="large"
                      formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(v) => Number((v || '').replace(/,/g, '')) as any}
                    />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="group" label="Nhóm (tùy chọn)">
                    <Input placeholder="Ví dụ: Cocktail" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="isSignature" valuePropName="checked">
                <Checkbox>
                  <Space>
                    <StarOutlined style={{ color: '#f59e0b' }} />
                    Đánh dấu là món Signature
                  </Space>
                </Checkbox>
              </Form.Item>

              <Form.Item label="Hình ảnh">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {branch2ImageUrl ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <Image
                        src={branch2ImageUrl}
                        width={160}
                        height={160}
                        style={{ objectFit: 'cover', borderRadius: 12 }}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={handleDeleteBranch2Image}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          background: 'rgba(255,255,255,0.85)',
                          borderRadius: 6,
                        }}
                      />
                    </div>
                  ) : null}
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleBranch2ImageUpload(file);
                      return false;
                    }}
                  >
                    <Button
                      icon={<UploadOutlined />}
                      loading={branch2ImageLoading}
                    >
                      {branch2ImageUrl ? 'Thay ảnh' : 'Tải ảnh lên'}
                    </Button>
                  </Upload>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </>
      ) : entryMode === 'invoice' ? (
        <>
          {/* Summary + controls */}
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col xs={24} sm={8} md={6}>
              <Card
                style={{
                  borderRadius: 12,
                }}
              >
                <Statistic
                  title={
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      Số dòng tháng {selectedMonth.format('MM/YYYY')}
                    </Text>
                  }
                  value={monthInvoices.length}
                  valueStyle={{ color: '#60a5fa', fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Card
                style={{
                  borderRadius: 12,
                }}
              >
                <Statistic
                  title={
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      Tổng số lượng
                    </Text>
                  }
                  value={totalQty}
                  valueStyle={{ color: '#34d399', fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Card
                style={{
                  borderRadius: 12,
                }}
              >
                <Statistic
                  title={
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      Doanh thu tháng
                    </Text>
                  }
                  value={totalRevenue}
                  formatter={(v) => formatVnd(Number(v))}
                  valueStyle={{ color: '#f59e0b', fontWeight: 700, fontSize: 18 }}
                />
              </Card>
            </Col>
            <Col
              xs={24}
              md={6}
              style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={(val) => val && setSelectedMonth(val)}
                format="MM/YYYY"
                allowClear={false}
                style={{ width: 140 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openAddModal}
                size="large"
                className="admin-button"
              >
                Thêm hóa đơn
              </Button>
            </Col>
          </Row>

          {/* Table */}
          <Card
            className="admin-table"
            style={{
              borderRadius: 12,
            }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              dataSource={monthInvoices}
              columns={columns}
              rowClassName={(_, index) => (index % 2 === 0 ? 'invoice-row-even' : 'invoice-row-odd')}
              rowKey={(inv) => inv.id || Math.random().toString()}
              loading={loading}
              pagination={{ pageSize: 30, showSizeChanger: false }}
              locale={{ emptyText: 'Chưa có hóa đơn nào trong tháng này' }}
              style={{ borderRadius: 12, overflow: 'hidden' }}
              scroll={{ x: 800 }}
            />
          </Card>

          {/* Add / Edit Modal */}
          <Modal
            title={
              <Space>
                <FileTextOutlined style={{ color: '#3b82f6' }} />
                {editingItem ? 'Sửa dòng hóa đơn' : 'Thêm dòng hóa đơn'}
                <Tag color="blue">{BRANCH2_STORE_LABEL}</Tag>
              </Space>
            }
            open={modalOpen}
            onCancel={() => {
              setModalOpen(false);
              form.resetFields();
            }}
            onOk={handleSave}
            okText={editingItem ? 'Cập nhật' : 'Lưu'}
            cancelText="Hủy"
            confirmLoading={saving}
            width={760}
            destroyOnClose
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                checkInDate: dayjs(),
                revenueSource: REVENUE_SOURCE_8AM,
                items: [{ quantity: 1 }],
              }}
            >
          <Form.Item
            name="revenueSource"
            label="Nguồn thu"
            rules={[{ required: true, message: 'Vui lòng chọn nguồn thu!' }]}
          >
            <Select
              options={REVENUE_SOURCE_OPTIONS}
              placeholder="Chọn nguồn thu"
            />
          </Form.Item>

          {/* Row 1: ngày + giờ */}
          <Row gutter={12}>
            <Col span={14}>
              <Form.Item
                name="checkInDate"
                label="Ngày bán"
                rules={[{ required: true, message: 'Chọn ngày!' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear={false} />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="checkInTime" label="Giờ (HH:mm)">
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  minuteStep={1}
                  placeholder="Chọn giờ"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: mã hóa đơn */}
          <Form.Item name="invoiceCode" label="Mã hóa đơn">
            <Input placeholder="Tự sinh hoặc nhập tay" />
          </Form.Item>

          <Form.List
            name="items"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || !value.length) {
                    throw new Error('Vui lòng thêm ít nhất 1 món.');
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 12, borderRadius: 10 }}
                    title={`Món ${index + 1}`}
                    extra={
                      !editingItem && fields.length > 1 ? (
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                          Xóa
                        </Button>
                      ) : null
                    }
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, 'itemId']}
                      label="Chọn món"
                      rules={[{ required: true, message: 'Vui lòng chọn món!' }]}
                    >
                      <Select
                        showSearch
                        placeholder="Tìm và chọn món..."
                        optionFilterProp="label"
                        options={menuItemOptions}
                        allowClear
                        onChange={(val) => {
                          const found = menuItems.find((m) => m.itemId === val);
                          const currentItems = (form.getFieldValue('items') || []) as InvoiceLineFormValues[];
                          const nextItems = [...currentItems];
                          const autoPrice = found?.defaultPrice ?? menuItemLookup.priceMap.get(val as string);
                          nextItems[index] = {
                            ...(nextItems[index] || { quantity: 1 }),
                            itemId: val || '',
                            itemName: found?.itemName || '',
                            selectedOptions: [],
                            unitPrice: autoPrice,
                          };
                          form.setFieldsValue({ items: nextItems });
                        }}
                      />
                    </Form.Item>

                    <Form.Item {...field} name={[field.name, 'itemName']} label="Tên món (tự điền)">
                      <Input readOnly style={{ background: '#f5f5f5' }} placeholder="Tự điền theo món đã chọn" />
                    </Form.Item>

                    <Form.Item shouldUpdate noStyle>
                      {({ getFieldValue }) => {
                        const selectedDishId = getFieldValue(['items', field.name, 'itemId']) as string | undefined;
                        const dishOptions = dishOptionsByCode.get(normalizeCode(selectedDishId)) || [];
                        const selectedOptions =
                          (getFieldValue(['items', field.name, 'selectedOptions']) as InvoiceOptionFormValue[] | undefined) || [];
                        const selectedOptionIds = selectedOptions.map((option) => option.optionId);

                        return (
                          <>
                            <Form.Item label="Tùy chọn món">
                              <Select
                                mode="multiple"
                                showSearch
                                allowClear
                                value={selectedOptionIds}
                                placeholder={selectedDishId ? 'Chọn tùy chọn cho món' : 'Chọn món trước để hiện tùy chọn'}
                                optionFilterProp="label"
                                options={dishOptions.map((option) => ({
                                  value: option.optionId,
                                  label: `${option.optionName} (${option.optionId})`,
                                }))}
                                disabled={!selectedDishId}
                                onChange={(nextOptionIds) => {
                                  const currentItems = (form.getFieldValue('items') || []) as InvoiceLineFormValues[];
                                  const nextItems = [...currentItems];
                                  const currentRow = nextItems[index] || { itemId: '', itemName: '', quantity: 1 };
                                  const existedOptions = currentRow.selectedOptions || [];
                                  const existedById = new Map(
                                    existedOptions
                                      .filter((option) => !!option.optionId)
                                      .map((option) => [option.optionId, option] as const),
                                  );

                                  const mergedOptions = (nextOptionIds as string[]).map((optionId) => {
                                    const existed = existedById.get(optionId);
                                    const optionMeta = dishOptions.find((option) => option.optionId === optionId);
                                    return {
                                      optionId,
                                      optionName: optionMeta?.optionName || existed?.optionName || optionCatalog.get(optionId) || optionId,
                                      unitPrice: existed?.unitPrice ?? optionMeta?.defaultPrice ?? 0,
                                    };
                                  });

                                  nextItems[index] = {
                                    ...currentRow,
                                    selectedOptions: mergedOptions,
                                  };

                                  form.setFieldsValue({ items: nextItems });
                                }}
                              />
                            </Form.Item>

                            {selectedOptions.length > 0 ? (
                              <Card
                                size="small"
                                style={{ marginBottom: 12, borderRadius: 8, background: '#fafcff' }}
                                title="Đơn giá tùy chọn"
                              >
                                <Space direction="vertical" style={{ width: '100%' }} size={10}>
                                  {selectedOptions.map((option, optionIndex) => (
                                    <Form.Item
                                      key={`${option.optionId}-${optionIndex}`}
                                      name={[field.name, 'selectedOptions', optionIndex, 'unitPrice']}
                                      label={`${option.optionName} (${option.optionId})`}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        placeholder="0"
                                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={(v) => Number((v || '').replace(/,/g, '')) as any}
                                      />
                                    </Form.Item>
                                  ))}
                                </Space>
                              </Card>
                            ) : null}
                          </>
                        );
                      }}
                    </Form.Item>

                    <Row gutter={12}>
                      <Col span={8}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'quantity']}
                          label="Số lượng"
                          rules={[{ required: true, message: 'Nhập số lượng!' }]}
                        >
                          <InputNumber style={{ width: '100%' }} min={1} placeholder="1" />
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item {...field} name={[field.name, 'unitPrice']} label="Đơn giá (đ) — nếu có">
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            placeholder="0"
                            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(v) => Number((v || '').replace(/,/g, '')) as any}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const qty = Number(getFieldValue(['items', field.name, 'quantity']) || 0);
                        const itemPrice = Number(getFieldValue(['items', field.name, 'unitPrice']) || 0);
                        const selectedOptions =
                          (getFieldValue(['items', field.name, 'selectedOptions']) as InvoiceOptionFormValue[] | undefined) || [];
                        const optionUnitTotal = selectedOptions.reduce(
                          (sum, option) => sum + Number(option?.unitPrice || 0),
                          0,
                        );
                        const rowTotal = qty * (itemPrice + optionUnitTotal);
                        if (!rowTotal) return null;
                        return (
                          <div
                            style={{
                              background: '#eff6ff',
                              border: '1px solid #bfdbfe',
                              borderRadius: 8,
                              padding: '8px 12px',
                              marginBottom: 12,
                              fontSize: 14,
                              color: '#1d4ed8',
                              fontWeight: 600,
                            }}
                          >
                            Thành tiền: {formatVnd(rowTotal)}
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                              (Giá món {formatVnd(itemPrice)} + option {formatVnd(optionUnitTotal)}) × {qty}
                            </Text>
                          </div>
                        );
                      }}
                    </Form.Item>

                    <Form.Item {...field} name={[field.name, 'note']} label="Ghi chú">
                      <Input.TextArea rows={2} placeholder="Ghi chú thêm nếu cần..." />
                    </Form.Item>
                  </Card>
                ))}

                {!editingItem && (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ itemId: '', itemName: '', selectedOptions: [], quantity: 1 })}
                    style={{ width: '100%', marginBottom: 8 }}
                  >
                    Thêm món trong hóa đơn
                  </Button>
                )}

                <Form.ErrorList errors={errors} />

                {/* Tổng tiền hóa đơn */}
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const allItems = (getFieldValue('items') || []) as InvoiceLineFormValues[];
                    let grandTotal = 0;
                    let itemCount = 0;

                    allItems.forEach((row) => {
                      if (!row?.itemId) return;
                      const qty = Number(row.quantity || 0);
                      const itemPrice = Number(row.unitPrice || 0);
                      const optionTotal = (row.selectedOptions || []).reduce(
                        (sum, opt) => sum + Number(opt?.unitPrice || 0),
                        0,
                      );
                      const rowTotal = qty * (itemPrice + optionTotal);
                      if (rowTotal > 0) {
                        grandTotal += rowTotal;
                        itemCount++;
                      }
                    });

                    if (!grandTotal) return null;

                    return (
                      <div
                        style={{
                          background: 'linear-gradient(135deg, #1d4ed8, #6366f1)',
                          borderRadius: 10,
                          padding: '14px 18px',
                          marginTop: 16,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>
                          Tổng hóa đơn ({itemCount} món)
                        </Text>
                        <Text
                          strong
                          style={{ color: '#fff', fontSize: 20 }}
                        >
                          {formatVnd(grandTotal)}
                        </Text>
                      </div>
                    );
                  }}
                </Form.Item>
              </>
            )}
          </Form.List>
            </Form>
          </Modal>
        </>
      ) : (
        <Card style={{ borderRadius: 12 }}>
          <IngredientManagement
            embedded
            initialTabKey="purchase-input"
            allowedTabKeys={['purchase-input']}
            hideNormalizationActions
          />
        </Card>
      )}
    </div>
  );
};

export default Branch2InvoiceEntry;
